// src/components/files/FileUploadZone.tsx
import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  alpha,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useTranslation } from 'react-i18next';
import { useFileManager } from '../../contexts/FileManagerContext';

interface FileUploadZoneProps {
  children: React.ReactNode;
}

const FileUploadZone: React.FC<FileUploadZoneProps> = ({ children }) => {
  const { t } = useTranslation();
  const { state, uploadDocument } = useFileManager();
  const { uploading, uploadProgress, section } = state;

  const [isDragging, setIsDragging] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [dragCounter, setDragCounter] = useState(0);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => {
      const newCounter = prev - 1;
      if (newCounter === 0) {
        setIsDragging(false);
      }
      return newCounter;
    });
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      setDragCounter(0);

      if (section !== 'my-space') return;

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          try {
            await uploadDocument(files[i]);
          } catch (error) {
            console.error('Upload failed:', error);
          }
        }
      }
    },
    [section, uploadDocument]
  );

  const canUpload = section === 'my-space';

  return (
    <Box
      onDragEnter={canUpload ? handleDragEnter : undefined}
      onDragLeave={canUpload ? handleDragLeave : undefined}
      onDragOver={canUpload ? handleDragOver : undefined}
      onDrop={canUpload ? handleDrop : undefined}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Upload progress bar */}
      {uploading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
          }}
        >
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{
              height: 4,
              bgcolor: alpha('#2563eb', 0.1),
              '& .MuiLinearProgress-bar': {
                bgcolor: 'primary.main',
              },
            }}
          />
        </Box>
      )}

      {/* Drag overlay */}
      {isDragging && canUpload && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: alpha('#2563eb', 0.08),
            border: '3px dashed',
            borderColor: 'primary.main',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            backdropFilter: 'blur(4px)',
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" color="primary.main" fontWeight={600}>
            {t('fileManager.upload.dropHere', 'Deposez vos fichiers ici')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('fileManager.upload.dropDescription', 'Les fichiers seront ajoutes au dossier actuel')}
          </Typography>
        </Box>
      )}

      {/* Children content */}
      {children}
    </Box>
  );
};

export default FileUploadZone;
